import { useEffect, useRef, type ReactNode } from 'react';
import { useT } from '../i18n';

export function Modal({ title, onClose, children, wide, actions }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean; actions?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  // Callers pass an inline onClose, so it changes on every parent render (live robot status re-renders pages
  // several times a second). Keep it in a ref: the effect below must run once, or it would move the focus back
  // to the first field / button while the user is typing in another one.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', onKey, true);
    if (!ref.current?.contains(document.activeElement)) {
      const first = ref.current?.querySelector<HTMLElement>('input, textarea, select, button');
      first?.focus();
    }
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={`dialog${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {children}
        {actions && <div className="dialog-actions">{actions}</div>}
      </div>
    </div>
  );
}

export function Battery({ pct, charging, large }: { pct: number | undefined; charging?: boolean; large?: boolean }) {
  const t = useT();
  if (typeof pct !== 'number') return null;
  const v = Math.max(0, Math.min(100, pct));
  const color = v < 20 ? 'var(--red)' : v < 40 ? 'var(--accent)' : 'var(--green)';
  return (
    <span className="row" style={{ gap: 6 }} title={charging ? t('robot.charging') : undefined}>
      <span className="mono" style={{ fontSize: large ? 13 : 12, color: large ? 'var(--text)' : 'var(--muted)' }}>
        {Math.round(v)}%
      </span>
      <span className={`battery${large ? ' lg' : ''}`} aria-hidden>
        <span style={{ width: `${v}%`, background: color }} />
      </span>
      {charging && <span style={{ color: 'var(--accent)', fontSize: 12 }}>⚡</span>}
    </span>
  );
}

export function Checkbox({ on }: { on: boolean }) {
  return (
    <span className={`checkbox${on ? ' on' : ''}`} aria-hidden>
      {on && (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1A1307" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          <path d="m5 12 5 5 9-10" />
        </svg>
      )}
    </span>
  );
}
